/**
 * EmailBlastModal — mirrors Angular EmailBroadcastComponent (frompage='users').
 * API: POST /api/email_broadcast (multipart/form-data when attachments present).
 *
 * Angular parity notes:
 *   - Single-step layout (no React-only confirm-scope step).
 *   - Required: seperated_to_address, email_subject, email_body.
 *   - passthru_mailgun hidden on cornerstone.libertyrent.com.
 *   - from_email visible only for ROLE_UNDERWRITER (default apps@libertyrent.com).
 *   - Reply-To defaults to current user's email.
 *   - Multiple attachments encoded as attachment[0], attachment[1], ...
 */
import { useState, useMemo, useEffect, useRef } from 'react'
import { X, Loader2 } from 'lucide-react'
import apiClient from '@/lib/api/apiClient'
import { API_ENDPOINTS } from '@/lib/api/config'
import { showSuccess, showApiError, showError } from '@/lib/utils/notifications'
import { trackApplicationEvent } from '@/lib/utils/analytics'
import { useAppSelector } from '@/app/hooks'
import { selectUser } from '@/features/auth/authSlice'
import { CKEditor4 } from '@/components/editor/CKEditor4'
import { useUserRole } from '@/hooks/useUserRole'
import { cn } from '@/lib/utils/helpers'
import { LIBERTY_FOCUS_INPUT } from '../utils/libertyAdminUi'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { primaryEntityActionButtonClass, secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import type { EmailBlastModalProps } from '../types'

// CKEditor returns markup like `<p></p>` or `<p>&nbsp;</p>` for visually-empty content.
// Strip tags/entities/whitespace before checking required-ness.
function isEffectivelyEmptyEmailHtml(html: string): boolean {
  if (!html?.trim()) return true
  const text = html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  return text.length === 0
}

const MAILGUN_HIDDEN_HOST = 'cornerstone.libertyrent.com'
const UNDERWRITER_DEFAULT_FROM = 'apps@libertyrent.com'
const MAX_ATTACHMENT_MB = 10

export function EmailBlastModal({
  isOpen,
  onClose,
  onSuccess,
  selectedIds,
  totalUsers,
  filters,
}: EmailBlastModalProps) {
  const currentUser = useAppSelector(selectUser)
  const { roleName } = useUserRole()
  const isUnderwriter = roleName === 'ROLE_UNDERWRITER'

  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [replyTo, setReplyTo] = useState(currentUser?.email ?? '')
  const [fromEmail, setFromEmail] = useState(isUnderwriter ? UNDERWRITER_DEFAULT_FROM : '')
  const [separated, setSeparated] = useState<'0' | '1'>('0')
  const [passthruMailgun, setPassthruMailgun] = useState<'0' | '1'>('0')
  const [sendSms, setSendSms] = useState(false)
  const [attachments, setAttachments] = useState<File[]>([])
  const [processing, setProcessing] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<{ subject?: string; body?: string }>({})
  const [errorMessage, setErrorMessage] = useState('')
  const [touched, setTouched] = useState<{ subject?: boolean; body?: boolean }>({})
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    if (currentUser?.email) {
      setReplyTo(currentUser.email)
    }
  }, [currentUser?.email])

  const showMailgun = useMemo(() => {
    if (typeof window === 'undefined') return true
    return window.location.hostname !== MAILGUN_HIDDEN_HOST
  }, [])

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const resetState = () => {
    setSubject('')
    setBody('')
    setReplyTo(currentUser?.email ?? '')
    setFromEmail(isUnderwriter ? UNDERWRITER_DEFAULT_FROM : '')
    setSeparated('0')
    setPassthruMailgun('0')
    setSendSms(false)
    setAttachments([])
    if (fileInputRef.current) fileInputRef.current.value = ''
    setProcessing(false)
    setFieldErrors({})
    setErrorMessage('')
    setTouched({})
    setSubmitted(false)
  }

  const handleClose = () => {
    resetState()
    onClose()
  }

  const recipientCount = selectedIds.length > 0 ? selectedIds.length : totalUsers
  const scopeMessage = `This Email will send to selected ${recipientCount} user`

  const updateValidation = (
    newSubject = subject,
    newBody = body,
    newTouched = touched,
    isSubmitted = submitted
  ) => {
    const subjectInvalid = !newSubject.trim()
    const bodyInvalid = isEffectivelyEmptyEmailHtml(newBody)

    const errors: { subject?: string; body?: string } = {}
    if (subjectInvalid) errors.subject = 'Subject is required'
    if (bodyInvalid) errors.body = 'Body is required'
    setFieldErrors(errors)

    if (subjectInvalid && (newTouched.subject || isSubmitted)) {
      setErrorMessage('Subject is required')
    } else if (bodyInvalid && (newTouched.body || isSubmitted)) {
      setErrorMessage('Body is required')
    } else {
      setErrorMessage('')
    }
  }

  const validate = () => {
    setSubmitted(true)
    const subjectInvalid = !subject.trim()
    const bodyInvalid = isEffectivelyEmptyEmailHtml(body)

    const errors: { subject?: string; body?: string } = {}
    if (subjectInvalid) errors.subject = 'Subject is required'
    if (bodyInvalid) errors.body = 'Body is required'
    setFieldErrors(errors)

    if (subjectInvalid) {
      setErrorMessage('Subject is required')
      return false
    }
    if (bodyInvalid) {
      setErrorMessage('Body is required')
      return false
    }
    setErrorMessage('')
    return true
  }

  const handleSend = async () => {
    if (!validate()) return

    const formData = new FormData()
    formData.append('email_subject', subject.trim())
    formData.append('email_body', body)
    formData.append('seperated_to_address', separated)
    formData.append('passthru_mailgun', passthruMailgun)
    formData.append('send_sms', sendSms ? 'true' : 'false')
    formData.append('frompage', 'users')
    formData.append('users_selected_id', '')
    if (replyTo.trim()) {
      formData.append('reply_to', encodeURIComponent(replyTo.trim()))
    }
    if (isUnderwriter && fromEmail.trim()) {
      formData.append('from_email', encodeURIComponent(fromEmail.trim()))
    }
    attachments.forEach((file, index) => {
      formData.append(`attachment[${index}]`, file)
    })

    if (selectedIds.length > 0) {
      selectedIds.forEach((id, index) => {
        formData.append(`user_ids[${index}]`, String(id))
      })
    } else {
      Object.entries(filters).forEach(([key, value]) => {
        if (['page', 'perPage', 'sortBy', 'orderBy', 'company_name', 'property_name'].includes(key)) return
        if (value !== undefined && value !== null && value !== '') {
          formData.append(key, String(value))
        }
      })
    }

    setProcessing(true)
    try {
      await apiClient.post(API_ENDPOINTS.EMAIL_BROADCAST, formData)
      trackApplicationEvent('broadcast_email_sent', 'email', { email_type: 'user_management' })
      showSuccess('Mail Sent Successfully!')
      onSuccess()
      handleClose()
    } catch (err) {
      showApiError(err)
    } finally {
      setProcessing(false)
    }
  }

  const handleEditorPaste = async (evt: any, instance: any) => {
    const data = evt?.data?.dataTransfer
    const files = data?.files || evt?.data?.clipboardData?.files
    if (files && files.length > 0) {
      const file = files[0]
      if (file && file.type?.startsWith('image/')) {
        setProcessing(true)
        const formData = new FormData()
        formData.append('emailcontent', file)
        try {
          const res = await apiClient.post<any>('/api/emailcontent_file_upload', formData)
          const imgUrl = typeof res === 'string' ? res : res?.data || res
          if (imgUrl && instance) {
            instance.insertHtml(`<img src="${imgUrl}" width="150px"></img>`)
          }
        } catch (err) {
          showApiError(err)
        } finally {
          setProcessing(false)
        }
      }
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files ?? [])
    if (newFiles.length === 0) return

    const oversize = newFiles.find((f) => f.size / 1024 / 1024 > MAX_ATTACHMENT_MB)
    if (oversize) {
      showError(`File Size Exceeds Allowable Limit of ${MAX_ATTACHMENT_MB} MB.`)
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    // Angular parity UM-EMAIL-04 / UM-EMAIL-07: each file pick replaces the selection
    setAttachments(newFiles)
  }

  const handleRemoveFile = (index: number) => {
    setAttachments((prev) => {
      const updated = prev.filter((_, i) => i !== index)
      if (updated.length === 0 && fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      return updated
    })
  }

  const handleClearFiles = () => {
    setAttachments([])
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  if (!isOpen) return null

  const inputCls = cn(
    'w-full rounded-lg border border-slate-200 px-3 py-2 text-sm',
    LIBERTY_FOCUS_INPUT
  )
  const inputErrorCls = 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
  const errorCls = 'mt-1 text-xs text-red-600'
  const radioWrapCls = 'flex items-center gap-4'
  const radioLabelCls = 'flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer'
  const attachmentLabel =
    attachments.length === 0
      ? ''
      : attachments.length === 1
        ? attachments[0].name
        : `${attachments.length} Files`

  return (
    <OnScreenModal
      isOpen={isOpen}
      title="Email Blast"
      headerClassName="!px-5 sm:!px-6"
      onClose={handleClose}
      maxWidth="max-w-3xl"
      panelClassName="sm:overflow-visible sm:max-h-none"
      bodyScrollOnly
      overlayDataTestId="um-email-blast-overlay"
      panelDataTestId="um-email-blast-modal"
      closeButtonDataTestId="um-email-blast-close-btn"
      titleDataTestId="um-email-blast-title"
      footer={
        <div className="flex items-center justify-center gap-3 w-full" data-testid="um-email-blast-footer">
          <button
            type="button"
            onClick={handleClose}
            className={secondaryEntityActionButtonClass}
            data-testid="um-email-blast-cancel-btn"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={processing}
            className={cn(primaryEntityActionButtonClass, 'px-6')}
            data-testid="um-email-blast-submit-btn"
          >
            {processing && <Loader2 className="h-4 w-4 shrink-0 animate-spin" />}
            {processing ? 'Sending...' : 'Submit'}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Top-of-form error banner (Angular parity UM-EMAIL-02) */}
        {errorMessage && (
          <div
            className="rounded-lg bg-red-50 p-3 text-sm font-medium text-red-600 border border-red-200"
            data-testid="um-email-blast-error-banner"
          >
            {errorMessage}
          </div>
        )}

        {/* Scope label (yellow strip banner matching Property Send Email popup) */}
        <div
          className="rounded-lg border border-[#fde68a] bg-[#fffbeb] px-4 py-3 text-sm font-semibold text-[#92400e] shadow-xs"
          data-testid="um-email-blast-scope-label"
        >
          {scopeMessage}
        </div>

        {/* Send Separately radio (required) */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Send Separately <span className="text-red-500">*</span>
          </label>
          <div className={radioWrapCls}>
            <label className={radioLabelCls}>
              <input
                type="radio"
                name="separated_to_address"
                value="1"
                checked={separated === '1'}
                onChange={() => setSeparated('1')}
                className="text-[rgb(21_151_255)]"
                data-testid="um-email-blast-separated-yes-radio"
              />
              Yes
            </label>
            <label className={radioLabelCls}>
              <input
                type="radio"
                name="separated_to_address"
                value="0"
                checked={separated === '0'}
                onChange={() => setSeparated('0')}
                className="text-[rgb(21_151_255)]"
                data-testid="um-email-blast-separated-no-radio"
              />
              No
            </label>
          </div>
        </div>

        {/* From Email — ROLE_UNDERWRITER only */}
        {isUnderwriter && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">From Email</label>
            <input
              type="text"
              className={inputCls}
              placeholder="From Email"
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              data-testid="um-email-blast-from-input"
            />
          </div>
        )}

        {/* Reply-To */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Reply To</label>
          <input
            type="email"
            className={inputCls}
            placeholder="Reply To"
            value={replyTo}
            onChange={(e) => setReplyTo(e.target.value)}
            data-testid="um-email-blast-reply-to-input"
          />
        </div>

        {/* Subject */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Email Subject <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            className={cn(inputCls, fieldErrors.subject && inputErrorCls)}
            placeholder="Email Subject"
            value={subject}
            onChange={(e) => {
              const val = e.target.value
              setSubject(val)
              updateValidation(val, body, touched, submitted)
            }}
            onBlur={() => {
              const newTouched = { ...touched, subject: true }
              setTouched(newTouched)
              updateValidation(subject, body, newTouched, submitted)
            }}
            data-testid="um-email-blast-subject-input"
          />
          {fieldErrors.subject && <p className={errorCls} data-testid="um-email-blast-subject-error">{fieldErrors.subject}</p>}
        </div>

        {/* Attachment (multiple) */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Attachment <span className="text-xs text-slate-400">(max {MAX_ATTACHMENT_MB} MB each)</span>
          </label>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileChange}
            data-testid="um-email-blast-attachment-input"
          />

          <div className="flex flex-col gap-2">
            <div
              className="flex items-center border border-[#d6e5ee] rounded-lg h-10 overflow-hidden bg-white shadow-sm hover:border-[#b4d2e7] transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
              data-testid="um-email-blast-attachment-trigger"
            >
              <div className="flex-1 px-3.5 py-2 text-sm text-[#253c5c] font-medium truncate select-none">
                {attachmentLabel || (
                  <span className="text-slate-400 font-normal">Choose file...</span>
                )}
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  fileInputRef.current?.click()
                }}
                className="bg-[#d8eafa] hover:bg-[#cce3f8] text-[#446990] text-sm font-semibold border-l border-[#d6e5ee] px-5 h-full flex items-center justify-center transition-colors shrink-0 cursor-pointer"
              >
                Upload
              </button>
            </div>

            {/* Selected Files List */}
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1" data-testid="um-email-blast-attachment-list">
                {attachments.map((file, idx) => (
                  <div
                    key={`${file.name}-${idx}`}
                    className="flex items-center gap-2 rounded-md border border-[#d6e5ee] bg-[#f4f9fd] px-2.5 py-1 text-xs font-medium text-[#253c5c]"
                    data-testid={`um-email-blast-attachment-item-${idx}`}
                  >
                    <span className="max-w-[14rem] truncate" title={file.name}>
                      {file.name}
                    </span>
                    <span className="text-[10px] text-slate-400 shrink-0">
                      ({(file.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleRemoveFile(idx)
                      }}
                      className="ml-1 rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600 transition-colors"
                      aria-label={`Remove ${file.name}`}
                      data-testid={`um-email-blast-remove-attachment-${idx}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                {attachments.length > 1 && (
                  <button
                    type="button"
                    onClick={handleClearFiles}
                    className="text-xs font-medium text-rose-600 hover:text-rose-700 px-1 py-1 transition-colors"
                    data-testid="um-email-blast-clear-attachments-btn"
                  >
                    Clear all
                  </button>
                )}
              </div>
            )}
            {/* Upload helper notice matching Property Send Email popup */}
            <div
              className="mt-2 bg-[#fef9ec] border border-[#eabf14] rounded-lg py-3 px-4 text-[#94845b] text-sm"
              role="alert"
              data-testid="um-email-blast-attachment-helper"
            >
              Use CTRL (windows)/CMD (apple) + Click to select multiple files to upload at one time.
            </div>
          </div>
        </div>

        {/* Body */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Email Body <span className="text-red-500">*</span>
          </label>
          <div
            className={cn(
              'rounded-md border border-slate-200 overflow-hidden bg-white',
              fieldErrors.body && inputErrorCls
            )}
            data-testid="um-email-blast-body-editor"
          >
            <CKEditor4
              id="email-blast-body"
              value={body}
              onChange={(html) => {
                setBody(html)
                const newTouched = { ...touched, body: true }
                setTouched(newTouched)
                updateValidation(subject, html, newTouched, submitted)
              }}
              onPaste={handleEditorPaste}
              config={{ height: 220 }}
            />
          </div>
          {fieldErrors.body && <p className={errorCls} data-testid="um-email-blast-body-error">{fieldErrors.body}</p>}
        </div>

        {/* Passthru Mailgun — hidden on cornerstone host */}
        {showMailgun && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Passthru Mailgun</label>
            <div className={radioWrapCls}>
              <label className={radioLabelCls}>
                <input
                  type="radio"
                  name="passthru_mailgun"
                  value="1"
                  checked={passthruMailgun === '1'}
                  onChange={() => setPassthruMailgun('1')}
                  className="text-[rgb(21_151_255)]"
                  data-testid="um-email-blast-mailgun-yes-radio"
                />
                Yes
              </label>
              <label className={radioLabelCls}>
                <input
                  type="radio"
                  name="passthru_mailgun"
                  value="0"
                  checked={passthruMailgun === '0'}
                  onChange={() => setPassthruMailgun('0')}
                  className="text-[rgb(21_151_255)]"
                  data-testid="um-email-blast-mailgun-no-radio"
                />
                No
              </label>
            </div>
          </div>
        )}

        {/* Send SMS notification */}
        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
          <input
            type="checkbox"
            checked={sendSms}
            onChange={(e) => setSendSms(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-[rgb(21_151_255)]"
            data-testid="um-email-blast-send-sms-checkbox"
          />
          Send Notification SMS
        </label>
      </div>
    </OnScreenModal>
  )
}
